import { BrowserRouter, Routes, Route, Navigate, useParams } from 'react-router-dom';
import { Suspense, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { HomePage } from '@/pages/HomePage';
import { ChatPage } from '@/pages/ChatPage';
import { LoginPage } from '@/pages/LoginPage';
import { SignupPage } from '@/pages/SignupPage';
import { AdminPage } from '@/pages/AdminPage';
import { VenueEditPage } from '@/pages/VenueEditPage';
import { AccountPage } from '@/pages/AccountPage';
import { PrivacyPage } from '@/pages/PrivacyPage';
import { TermsPage } from '@/pages/TermsPage';
import { ContactPage } from '@/pages/ContactPage';
import { LineKBPage } from '@/pages/LineKBPage';
import { LineBotChatPage } from '@/pages/LineBotChatPage';
import { GeminiKBAdminPage } from '@/pages/GeminiKBAdminPage';
import { GeminiKBChatPage } from '@/pages/GeminiKBChatPage';
import { Header } from '@/components/Header';
import { Footer } from '@/components/Footer';
import { useDocumentLang } from '@/hooks/useDocumentLang';
import { AuthProvider } from '@/hooks/auth-context';

// Layout wrapper component - includes header and footer
const MainLayout = ({ children }: { children: React.ReactNode }) => {
  return (
    <div className="min-h-screen flex flex-col">
      <Header />
      <main className="flex-1">{children}</main>
      <Footer />
    </div>
  );
};

// Chat layout - no header/footer, full screen
const ChatLayout = ({ children }: { children: React.ReactNode }) => {
  return <>{children}</>;
};

// Admin layout - no header/footer, page has its own navigation
const AdminLayout = ({ children }: { children: React.ReactNode }) => {
  return <>{children}</>;
};

// Language wrapper component to validate and pass lang param
function LanguageRoutes() {
  const { lang } = useParams<{ lang: string }>();
  const { i18n } = useTranslation();
  const validLangs = ['en', 'ja', 'zh'];

  // Sync i18n language with URL parameter
  // Must be called before any early returns
  useEffect(() => {
    if (lang && lang !== i18n.language) {
      i18n.changeLanguage(lang);
    }
  }, [lang, i18n]);

  // Validate language (after hook call)
  if (lang && !validLangs.includes(lang)) {
    return <Navigate to="/ja" replace />;
  }

  return (
    <Routes>
      {/* Public Pages (with Header/Footer) */}
      <Route path="/" element={<MainLayout><HomePage /></MainLayout>} />
      <Route path="/login" element={<MainLayout><LoginPage /></MainLayout>} />
      <Route path="/signup" element={<MainLayout><SignupPage /></MainLayout>} />
      <Route path="/admin" element={<AdminLayout><AdminPage /></AdminLayout>} />
      <Route path="/edit/:venueId" element={<AdminLayout><VenueEditPage /></AdminLayout>} />
      <Route path="/account" element={<MainLayout><AccountPage /></MainLayout>} />
      <Route path="/privacy" element={<MainLayout><PrivacyPage /></MainLayout>} />
      <Route path="/terms" element={<MainLayout><TermsPage /></MainLayout>} />
      <Route path="/contact" element={<MainLayout><ContactPage /></MainLayout>} />

      {/* Chat Pages (Full-screen, no Header/Footer) */}
      {/* Multitenant chat routes
          Pattern: /talk/:identifier (primary - uses short code or UUID)
          Pattern: /talk/:identifier/:vanitySlug (legacy - with optional vanity slug for SEO)
          Example: /ja/talk/h3j7km2p (short code - preferred)
          Example: /ja/talk/550e8400-e29b-41d4-a716-446655440000 (legacy UUID - will auto-resolve)
          - identifier: Short code (8 chars) or UUID
      */}
      <Route path="/talk/:identifier" element={<ChatLayout><ChatPage /></ChatLayout>} />
      <Route path="/talk/:identifier/:vanitySlug" element={<ChatLayout><ChatPage /></ChatLayout>} />

      {/* Simple demo route without venue */}
      <Route path="/demo" element={<ChatLayout><ChatPage /></ChatLayout>} />

      {/* Fallback - redirect to home with language */}
      <Route path="*" element={<Navigate to={`/${lang}`} replace />} />
    </Routes>
  );
}

export function App() {
  useDocumentLang(); // Update HTML lang attribute based on i18n language

  return (
    <BrowserRouter>
      <AuthProvider>
        <Suspense fallback={
          <div className="min-h-screen bg-background flex items-center justify-center">
            <div className="text-center">
              <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4" />
              <p className="text-gray-600">Loading...</p>
            </div>
          </div>
        }>
          <Routes>
            {/* Root redirect to default language */}
            <Route path="/" element={<Navigate to="/ja" replace />} />

            {/* LINE Bot knowledge base admin (no language prefix, password-protected) */}
            <Route path="/line-kb" element={<LineKBPage />} />

            {/* LINE bot web chat frontend */}
            <Route path="/line-chat" element={<LineBotChatPage />} />

            {/* Gemini File Search KB — admin + chat (no language prefix) */}
            <Route path="/kb-admin/:shortCode" element={<GeminiKBAdminPage />} />
            <Route path="/kb-chat/:shortCode" element={<GeminiKBChatPage />} />

            {/* Language-based routes */}
            <Route path="/:lang/*" element={<LanguageRoutes />} />

            {/* Catch all */}
            <Route path="*" element={<Navigate to="/ja" replace />} />
          </Routes>
        </Suspense>
      </AuthProvider>
    </BrowserRouter>
  );
}
